import React from 'react';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';

const MissionSection = () => {
  const values = [
    {
      icon: "Target",
      title: "Mission-Driven",
      description: "Democratizing access to cutting-edge technology education and creating pathways for career transformation in AI, Robotics, and Advanced Programming."
    },
    {
      icon: "Eye",
      title: "Visionary Approach",
      description: "Building the future workforce by bridging the gap between traditional education and industry demands through innovative, community-powered learning."
    },
    {
      icon: "Heart",
      title: "Core Values",
      description: "Excellence through collaboration, continuous learning, practical application, and unwavering commitment to student success and community growth."
    }
  ];

  return (
    <section className="py-20 bg-card/30">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16">
          <div className="inline-flex items-center px-4 py-2 rounded-full bg-cyber-purple/10 border border-cyber-purple/20 mb-6">
            <Icon name="Compass" size={16} className="text-cyber-purple mr-2" />
            <span className="text-sm font-medium text-cyber-purple">Our Foundation</span>
          </div>
          
          <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-6">
            Mission, Vision & Values
          </h2>
          
          <p className="text-lg text-muted-foreground max-w-3xl mx-auto">
            Founded on the belief that technology education should be accessible, practical, and transformative. We're building more than just a learning platform—we're cultivating a movement.
          </p>
        </div>

        {/* Values Grid */}
        <div className="grid md:grid-cols-3 gap-8 mb-16">
          {values.map((value, index) => (
            <div key={index} className="holographic-card p-8 text-center group hover:scale-105 transition-transform duration-300">
              <div className="w-16 h-16 bg-gradient-to-br from-accent to-cyber-purple rounded-full flex items-center justify-center mx-auto mb-6 shadow-glow group-hover:shadow-glow-strong transition-all duration-300">
                <Icon name={value.icon} size={24} color="white" strokeWidth={2.5} />
              </div>
              
              <h3 className="text-xl font-bold text-foreground mb-4">{value.title}</h3>
              <p className="text-muted-foreground leading-relaxed">{value.description}</p>
            </div>
          ))}
        </div>

        {/* Founding Story */}
        <div className="bg-gradient-to-r from-primary to-secondary rounded-2xl p-8 md:p-12 border border-border">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h3 className="text-2xl md:text-3xl font-bold text-foreground mb-6">
                The Dream That Started It All
              </h3>
              
              <div className="space-y-4 text-muted-foreground">
                <p>
                  In 2015, our founder recognized a critical gap in technology education. Traditional institutions were falling behind industry needs, while expensive bootcamps remained inaccessible to many passionate learners.
                </p>
                
                <p>
                  The vision was simple yet revolutionary: create a community-driven platform where learning happens through collaboration, mentorship, and real-world projects. Where students don't just consume content—they create, innovate, and transform together.
                </p>
                
                <p>
                  Today, Dream Coders Club stands as proof that when you combine cutting-edge curriculum with genuine community support, extraordinary transformations happen.
                </p>
              </div>
              
              <div className="flex items-center mt-8 pt-6 border-t border-border">
                <div className="flex items-center space-x-3 mb-3">
                  <Image
                    src="/assets/images/avatars/chirag_logo.jpeg"
                    className="w-20 h-20 rounded-full"
                  />
                </div>
                <div className="ml-4">
                  <div className="text-sm font-medium text-foreground">Founded by Chirag Sharma</div>
                  <div className="text-xs text-muted-foreground">10+ years in tech & education</div>
                </div>
              </div>
            </div>
            
            <div className="relative">
              <div className="aspect-square rounded-2xl bg-gradient-to-br from-accent/20 to-cyber-purple/20 flex items-center justify-center">
                <div className="text-center">
                  <Icon name="Lightbulb" size={80} className="text-accent mx-auto mb-4" />
                  <div className="text-4xl font-bold text-foreground mb-2">2015</div>
                  <div className="text-muted-foreground">Year Founded</div>
                </div>
              </div>
              
              {/* Floating Stats */}
              <div className="absolute -top-4 -right-4 bg-card/90 backdrop-blur-sm rounded-lg p-3 border border-border shadow-elevated">
                <div className="text-lg font-bold text-success">10 Years</div>
                <div className="text-xs text-muted-foreground">of Innovation</div>
              </div>
              
              <div className="absolute -bottom-4 -left-4 bg-card/90 backdrop-blur-sm rounded-lg p-3 border border-border shadow-elevated">
                <div className="text-lg font-bold text-cyber-purple">Global</div>
                <div className="text-xs text-muted-foreground">Community</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default MissionSection;